import React, { useCallback, useEffect, useRef, useState } from 'react'
import './App.css'
import GameStatsView from './components/GameStatsView'
import LandingView from './components/LandingView'
import { Game, Srm } from './srm/srm-parser'
import { Buffer } from 'buffer'

const SRM_FILE_SIZE = 0x2000

interface Toast {
  id: number
  message: string
}

function App() {
  const [srm, setSrm] = useState<Srm | null>(null)
  const [games, setGames] = useState<{ a: Game; b: Game; c: Game } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [toasts, setToasts] = useState<Toast[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toastIdRef = useRef(0)
  const dragCounter = useRef(0)

  const showError = useCallback((message: string) => {
    const id = ++toastIdRef.current
    setToasts((prev) => [...prev, { id, message }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  const processFile = useCallback(
    (file: File) => {
      if (!file.name.toLowerCase().endsWith('.srm')) {
        showError(`"${file.name}" is not an .srm file`)
        return
      }
      file.arrayBuffer().then((buffer) => {
        if (buffer.byteLength !== SRM_FILE_SIZE) {
          showError(
            `Invalid file size: expected ${SRM_FILE_SIZE} bytes, got ${buffer.byteLength}`
          )
          return
        }
        try {
          const parser = new Srm(Buffer.from(buffer))
          setSrm(parser)
          setGames(parser.games)
        } catch (e) {
          showError(
            `Failed to parse file: ${e instanceof Error ? e.message : String(e)}`
          )
        }
      })
    },
    [showError]
  )

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      dragCounter.current = 0
      setIsDragging(false)
      const files = event.dataTransfer?.files
      if (files && files.length > 0) {
        processFile(files[0])
      }
    },
    [processFile]
  )

  const handleDragEnter = useCallback((event: DragEvent) => {
    event.preventDefault()
    dragCounter.current++
    setIsDragging(true)
  }, [])

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
  }, [])

  const handleDragLeave = useCallback((_event: DragEvent) => {
    dragCounter.current--
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('drop', handleDrop)
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('dragleave', handleDragLeave)
    return () => {
      document.removeEventListener('drop', handleDrop)
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('dragleave', handleDragLeave)
    }
  }, [handleDrop, handleDragEnter, handleDragOver, handleDragLeave])

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) processFile(file)
    event.target.value = ''
  }

  const handleOpenFile = () => fileInputRef.current?.click()

  const handleExport = useCallback(
    (exportGames: { a: Game; b: Game; c: Game }) => {
      if (!srm) return
      const data = srm.serialize(exportGames)
      const blob = new Blob([data.buffer as ArrayBuffer], {
        type: 'application/octet-stream'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'save.srm'
      a.click()
      URL.revokeObjectURL(url)
    },
    [srm]
  )

  return (
    <div className="App">
      <input
        ref={fileInputRef}
        type="file"
        accept=".srm"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />

      <div className="toast-container">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="toast"
            onClick={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
          >
            {toast.message}
          </div>
        ))}
      </div>

      {games ? (
        <GameStatsView
          games={games}
          onOpenFile={handleOpenFile}
          onExport={handleExport}
          isDragging={isDragging}
        />
      ) : (
        <LandingView isDragging={isDragging} onOpenFile={handleOpenFile} />
      )}
    </div>
  )
}

export default App
